import { useDispatch, useSelector } from "react-redux";
import { useProposalTypeData } from "../../../../../hooks/proposal/use-proposal-type-data";
import { selectedProposalPreparedByName } from "../../../../../features/gms-state-and-modal/state-slice";
import { setUpdateTypeModal } from "../../../../../features/gms-state-and-modal/modal-slice";
import { selectApprovalData } from "../../../../../features/actuarial/approval/approval-slice";

interface Props {
    isOpen: boolean;
    onConfirm: () => void;
    onDeny: () => void;
    onClose: () => void;
    isLoading?: boolean;
}

export default function ComputationApprovalDataModal({ isOpen, onConfirm, onDeny, onClose, isLoading = false, }: Props) {

    const dispatch = useDispatch()
    const { 
        isCustomize,
        prototype,
        product,
        basicInformation,
        natureOfBusinessCategory,
        contactPersonSalutation: salutation,
        contactPersonFirstName: firstName,
        contactPersonMiddleName: middleName,
        contactPersonLastName: lastName,
        proposalTypeState,
    } = useProposalTypeData();
    const proposalPreparedBy = useSelector(selectedProposalPreparedByName);
    const middleInitial = middleName ? `${middleName.charAt(0)}.` : '';
    const contractPersonFormat = `${salutation ?? ''} ${firstName ?? ''} ${middleInitial} ${lastName ?? ''}`.replace(/\s+/g, ' ').trim();
    const productName = isCustomize ? product?.name : prototype?.name;
    const { endorsed_by_name, endorsed_to_name, special_actuarial_notes } = useSelector(selectApprovalData);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
            <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-900">
                
                <div className="flex shrink-0 items-start justify-between border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900 sm:px-5">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-sm font-semibold text-slate-800 dark:text-white">
                                Computation Approval
                            </h2>
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-[#186cb2] dark:bg-blue-950/40">
                                {'For Approval'}
                            </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Review the proposal and computation before making
                            your decision.
                        </p>
                    </div>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onClose}
                        className="ml-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                        aria-label="Close"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" >
                            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="space-y-3 p-4 sm:p-5">
                        {/* Application Summary */}
                        {/* <section className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                        Status
                                    </p>
                                    <p className="mt-0.5 text-xs font-semibold text-slate-800 dark:text-white">
                                        For Approval
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                        Submitted By (CFE)
                                    </p>
                                    <p className="mt-0.5 text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {proposalPreparedBy ?? '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Group
                                    </p>
                                    <p className="mt-1 truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {basicInformation.groupName ?? '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Contact Person
                                    </p>
                                    <p className="mt-1 truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {contractPersonFormat ?? '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Email
                                    </p>
                                    <p className="mt-1 truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {basicInformation.email ?? '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Proposal Type
                                    </p>
                                    <p className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {proposalTypeState?.name ?? '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Product
                                    </p>
                                    <p className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {productName ?? '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                        Nature of business
                                    </p>
                                    <p className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                                        {natureOfBusinessCategory?.name ?? '-'}
                                    </p>
                                </div>
                            </div>
                        </section> */}

                        {/* Review Buttons */}
                        <section>
                            <div className="mb-2">
                                <h3 className="text-xs font-semibold text-slate-800 dark:text-white">
                                    Review
                                </h3>

                                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                    Review the proposal and computation before
                                    approving this request.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => dispatch(setUpdateTypeModal('showProposalDetails'))}
                                    disabled={isLoading}
                                    className="group flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:border-[#186cb2] hover:bg-[#eef6fc] disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-[#186cb2]"
                                >
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eef6fc] text-[#186cb2] dark:bg-blue-950/40">
                                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                            <path d="M6 3h9l4 4v14H6V3z" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 3v5h5M9 13h6M9 17h6" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold text-slate-800 group-hover:text-[#186cb2] dark:text-white">
                                            View Proposal Data
                                        </p>
                                        <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                            Review applicant and proposal
                                            information.
                                        </p>
                                    </div>
                                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#186cb2]" fill="none" stroke="currentColor" strokeWidth="2" >
                                        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => dispatch(setUpdateTypeModal('showComputationDetails'))}
                                    disabled={isLoading}
                                    className="group flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:border-[#186cb2] hover:bg-[#eef6fc] disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-[#186cb2]"
                                >
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eef6fc] text-[#186cb2] dark:bg-blue-950/40">
                                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                            <path d="M4 19V5M4 19h16" strokeLinecap="round" />
                                            <path d="M8 16v-5M12 16V8M16 16v-8" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold text-slate-800 group-hover:text-[#186cb2] dark:text-white">
                                            View Rates
                                        </p>
                                        <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                            Review rates, premiums and
                                            computation details.
                                        </p>
                                    </div>
                                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#186cb2]" fill="none" stroke="currentColor" strokeWidth="2" >
                                        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </section>
                        <section className="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm dark:border-amber-900/60 dark:bg-gray-900">
                            <div className="flex items-center gap-2.5 border-b border-amber-100 bg-amber-50/70 px-3.5 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                                        Approval Details
                                    </p>
                                    <p className="mt-0.5 text-[10px] text-amber-700/80 dark:text-amber-300/70">
                                        Review the submission before taking an approval action.
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-3 p-3.5">
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <div className="group rounded-lg border border-gray-200 bg-gray-50/70 p-3 transition hover:border-[#186cb2]/20 hover:bg-[#186cb2]/5 dark:border-gray-700 dark:bg-gray-800/50">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10 text-[#186cb2]">
                                                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                                    <path d="M20 21a8 8 0 0 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                                    Endorsed By
                                                </p>
                                                <p className="mt-0.5 truncate text-xs font-semibold text-gray-800 dark:text-gray-200">
                                                    {endorsed_by_name || "Not available"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="group rounded-lg border border-gray-200 bg-gray-50/70 p-3 transition hover:border-[#186cb2]/20 hover:bg-[#186cb2]/5 dark:border-gray-700 dark:bg-gray-800/50">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10 text-[#186cb2]">
                                                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                                    <path d="M5 12h13M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                                    Endorsed To
                                                </p>
                                                <p className="mt-0.5 truncate text-xs font-semibold text-gray-800 dark:text-gray-200">
                                                    {endorsed_to_name || "Not available"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                                    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                                        <div className="flex items-center gap-2">
                                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                                <path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20V3.5Z" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14 3.5V8h4M9.5 12h5M9.5 15h5" strokeLinecap="round" />
                                            </svg>
                                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                Requester Notes
                                            </p>
                                        </div>
                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                            Actuarial
                                        </span>
                                    </div>
                                    <div className="px-3 py-3">
                                        {special_actuarial_notes?.trim() ? (
                                            <p className="whitespace-pre-wrap text-xs leading-5 text-gray-600 dark:text-gray-300">
                                                {special_actuarial_notes}
                                            </p>
                                        ) : (
                                            <div className="flex items-center gap-2 py-1">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                                                    <svg viewBox="0 0 24 24" className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                                        <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
                                                        <circle cx="12" cy="12" r="9" />
                                                    </svg>
                                                </div>
                                                <p className="text-[11px] text-gray-400">
                                                    No requester notes available.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900 sm:px-5">
                    {/* {!denyMode ? ( */}
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={onClose}
                                className="w-full rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 sm:w-auto"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={onDeny}
                                className="w-full rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/60 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30 sm:w-auto"
                            >
                                Deny
                            </button>
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={onConfirm}
                                className="w-full rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-[#125b99] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                            >
                                {/* {isLoading
                                    ? "Processing..."
                                    : "Accept Computation"} */}
                                    Accept Computation
                            </button>
                        </div>
                    {/* ) : (
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => {
                                    setDenyMode(false);
                                    dispatch(setApproverNotes(''))
                                }}
                                className="w-full rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 sm:w-auto"
                            >
                                Back
                            </button>

                            <button
                                type="button"
                                disabled={isLoading || !notes.trim()}
                                onClick={handleDeny}
                                className="w-full rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                            >
                                Confirm Denial
                            </button>
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
}